import Head from 'next/head';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import Layout from '../../../components/Layout';
import Hero from '../../../components/Home/Hero';
import BoxHeading from '../../../components/UI/BoxHeading';
import SquarePattern from '../../../components/UI/SquarePattern';
import MasonryCard from '../../../components/UI/MasonryCard';
import Testimonial from '../../../components/UI/Testimonial';
import StatsWithIcons from '../../../components/UI/StatsWithIcons';
import TabsCarousel from '../../../components/UI/TabsCarousel';
import Calculator from '../../../components/DemandLP/SalaryCalculator/Calculator';

import metaRobots from '../../../helpers/metaRobots';

import sharecardImage from '../../../public/images/sharecard.jpg';
import { Box, Container, Grid, GridItem } from '@chakra-ui/react';
import sitePath from '../../../helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const { publicRuntimeConfig } = getConfig();
const { talentHubOnboardingUrl } = publicRuntimeConfig;

const hero = {
  longText: true,
  smallText: true,
  title: {
    formatted:
      'Hire developers in Chile for <span style="color: #96BA6E">top engineering talent</span>',
    main: '',
  },
  text: 'An emerging e-commerce leader with tech hubs like Santiago, Chile has a large pool of experienced software engineers. Hire elite developers, mobile app developers, QA engineers, and more in Chile 40% faster with Terminal.',
  image: {
    sourceUrl: '/images/hiring-markets/chile.svg',
    altText: 'Distinctive buidings in Chile, where Terminal hires world-class software engineers',
  },
  button: {
    url: talentHubOnboardingUrl,
    text: 'Browse Talent',
    width: '15rem',
  },
};

const statsTitle = 'Highly qualified talent at scale';
const stats = [
  {
    image: {
      sourceUrl: '/images/icons/icon-avatars-paper-blue.svg',
      altText: 'Engineers with open role illustration',
    },
    item: '27',
    text: 'Approximately 27 engineers per open role',
  },
  {
    image: {
      sourceUrl: '/images/icons/icon-avatars-plus-yellow.svg',
      altText: 'Engineers in talent pool illustration',
    },
    item: '75,000',
    text: 'Roughly 75,000 engineers in a rapidly expanding talent pool',
  },
  {
    image: {
      sourceUrl: '/images/icons/icon-briefcase.svg',
      altText: 'Briefcase illustration',
    },
    item: '10+ years',
    text: 'Seasoned professionals with over 10 years experience, on average',
  },
];

const boxHeading = {
  title:
    'The newest addition to Terminal’s roster of global hiring hotspots, Chile is rich with seasoned engineers.',
  text: 'Powered by ambitious investment programs and start-up accelerators, the country is also emerging as one of the world’s most promising centers for fintech and e-commerce innovation. Chile has globally ranked universities throughout, feeding best-in-class engineers and developers into its already substantial talent pipeline.',
  color: 'green.500',
};

const masonryCards = [
  {
    type: 'content_image',
    position: false,
    bgDark: true,
    image: {
      sourceUrl: '/images/hiring-markets/time-zone-alignment-chile.svg',
      altText: 'Engineers from across the globe connect in a virtual meeting',
    },
    title: 'Time zone alignment',
    text: [
      {
        strong: '',
        normal:
          'Time zones matter – for meetings, for collaboration and for moving projects forward. Like all talent in Terminal’s hiring markets, remote workers in Chile are ideally situated for alignment with US time zones.',
      },
    ],
  },
  {
    type: 'content_image',
    position: true,
    bgDark: true,
    image: {
      sourceUrl: '/images/hiring-markets/chile-fully-remote.jpg',
      altText: 'Female engineer works from home and connects with colleagues virtually',
    },
    title: 'Expert engineering teams, fully remote',
    text: [
      {
        strong: '',
        normal:
          'Terminal handles all onboarding and admin for your new hires, and once they’re on board, we continue delivering the expert support they need. In addition to payroll, IT and localized HR, we offer home office perks to keep remote teams happy and highly productive, including workspace budgets, ergonomic assessments and an internet stipend.',
      },
    ],
  },
  {
    type: 'content_image',
    position: true,
    bgDark: false,
    image: {
      sourceUrl: '/images/hiring-markets/why-terminal.jpg',
      altText: 'A computer screen with remote engineers in a Zoom meeting',
    },
    title: 'Why Terminal',
    text: [
      {
        strong: '',
        normal:
          'When you partner with Terminal for hiring, you tap into top talent markets across Chile and throughout Mexico, Latin America, Canada and Europe. Our recruiters are based in the markets they’re hiring in – they understand regional interview and workplace culture, and they maintain local relationships to continue expanding our talent networks. Terminal serves as the employer of record for the professionals you decide to add to your team, taking on all legal risk and administrative burden.',
      },
    ],
  },
];

const testimonial = {
  bgDark: false,
  image: {
    sourceUrl: '/images/testimonials/andrew-backes.jpg',
    altText: 'Andrew Backes',
  },
  text: '“Terminal has that expertise, and because of that, I haven’t had to spend as much time learning the hard way. I don’t have to learn a new market through trial and error. I can work with Terminal, expand our engineering team, and have more engineering throughput.”',
  author: {
    name: 'Andrew Backes',
    jobTitle: 'Head of Engineering at Armory',
  },
};

const tabsCarousel = {
  type: 'pin',
  title: 'Where We Hire',
  text: 'Terminal is connecting top engineers across Canada, Europe and Latin America with companies on the very forefront of innovation.',
  defaultIndex: 3,
  tabs: [
    {
      image: {
        sourceUrl: '/images/hiring-markets/where-we-hire-canada.svg',
      },
      altText: 'Canadian engineer stands in front of whiteboard',
      title: 'Canada',
      text: 'A premier global market for hiring top-tier engineers, Canada has innovative tech culture and thriving talent ecosystems in Vancouver, Toronto, and Kitchener-Waterloo',
    },
    {
      image: {
        sourceUrl: '/images/hiring-markets/where-we-hire-mexico.svg',
      },
      altText: 'Mexican engineer stands in front of desk',
      title: 'Mexico',
      text: 'With competitive tech universities throughout the country and tech hubs like Guadalajara, Mexico is a leading global market for skilled engineers',
    },
    {
      image: {
        sourceUrl: '/images/hiring-markets/where-we-hire-colombia.svg',
      },
      altText: 'Colombian engineer stands in front of whiteboard',
      title: 'Colombia',
      text: 'One of South America’s largest and fastest-growing tech centers, Colombia has strong ecosystems for engineering talent cities like Bogotá and Medellín',
    },
    {
      image: {
        sourceUrl: '/images/hiring-markets/where-we-hire-chile.svg',
      },
      altText: 'Chilean engineer stands in front of whiteboard',
      title: 'Chile',
      text: 'Home to Latin America’s top tech accelerator and elite engineering programs, Chile has a highly-seasoned pool of developer talent',
    },
    {
      image: {
        sourceUrl: '/images/hiring-markets/where-we-hire-poland.svg',
      },
      altText: 'Polish engineer stands in front of whiteboard',
      title: 'Poland',
      text: 'From Warsaw and Krakow to Wroclaw and Lodz, Poland boasts some of the hottest engineering talent in Eastern Europe',
    },
    {
      image: {
        sourceUrl: '/images/hiring-markets/where-we-hire-costa-rica.svg',
      },
      altText: 'Costa Rica engineer stands in front of whiteboard',
      title: 'Costa Rica',
      text: 'Driving an enviable startup ecosystem, Costa Rica has already caught the attention of ambitious US companies looking to benefit from this spirited culture of creative problem-solving and original thinking',
    },
    {
      image: {
        sourceUrl: '/images/hiring-markets/where-we-hire-spain.svg',
      },
      altText: 'Spanish engineer stands in front of whiteboard',
      title: 'Spain',
      text: 'As the fifth-largest metropolitan area in Europe, Barcelona exemplifies why Spain is a massive hub of innovation, powered by top universities and well-established tech talent',
    },
  ],
};

const calculatorTheme = {
  green100: 'green.100',
  green200: 'green.500',
  green300: 'grey.900',
};

const footer = {
  longText: true,
  text: 'Ready to start building your team with top talent in Chile and beyond?',
};

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default function Chile() {
  const router = useRouter();

  const meta = {
    title: 'Hire Remote Developers in Chile - Terminal.io',
    description:
      'Hire elite remote software developers in Chile with Terminal. We source, vet, and manage payroll & benefits for full-time & contract engineers in Chile.',
  };

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <Layout type="businesses" footer={footer} withCTA>
        {hero && (
          <Box bgColor="grey.100">
            <Hero hero={hero} />
          </Box>
        )}
        {stats && stats.length && <StatsWithIcons stats={stats} title={statsTitle} />}
        <SquarePattern
          mt={{ base: '0.25rem', lg: '0.75rem' }}
          mb={{ base: '-18.4375rem', lg: '-14.25rem' }}
        />
        {boxHeading && (
          <BoxHeading
            title={boxHeading.title}
            text={boxHeading.text}
            color={boxHeading.color}
            maxW="60rem"
          />
        )}
        {masonryCards && masonryCards.length && (
          <>
            <Grid gap={{ base: '14', lg: '6' }}>
              {masonryCards.slice(0, 2).map((card) => (
                <GridItem key={card.title}>
                  <MasonryCard card={card} />
                </GridItem>
              ))}
            </Grid>
            <SquarePattern mt={{ base: '-18.75rem', lg: '-14.5rem' }} />
          </>
        )}
        {masonryCards && masonryCards.length && (
          <>
            <Grid gap={{ base: '14', lg: '6' }} mt={{ base: '14', lg: '24' }}>
              {masonryCards.slice(masonryCards.length - 1).map((card) => (
                <GridItem key={card.title}>
                  <MasonryCard card={card} />
                </GridItem>
              ))}
            </Grid>
          </>
        )}
        {testimonial && (
          <>
            <Container mt={{ base: '5' }} mb={{ base: '5' }}>
              <Testimonial
                testimonial={testimonial}
                pt="16"
                pb="20"
                px={{ base: '16', lg: '20' }}
              />
            </Container>
          </>
        )}
        {tabsCarousel && tabsCarousel.tabs.length && <TabsCarousel tabsCarousel={tabsCarousel} />}
        <Box mt={{ base: '10', lg: '24' }}>
          <Calculator theme={calculatorTheme} withStart={false} rounded={false} />
        </Box>
      </Layout>
    </>
  );
}
